import React, { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  nasalSymptomsEnum,
  nasalCongestionEnum,
  visibleSymptomsEnum,
  otherSymptomsEnum,
} from "./data";
import Button from "../../../../common/Button";
import {
  setClinicalServiceSteps,
  updateClinicalService,
} from "../../../../../redux/modules/clinical-services/actions";
import ChartNav from "../../../../common/ChartNav";
import EndServiceModel from "../../../../common/EndServiceModel";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import AllergensAlert from "./AllergensAlert";
import {
  AlertConfirmationTypes,
  MinorAilmentStep,
} from "../../../../../helpers/constant";
import { scrollX } from "../../../../../helpers/functions";
import SubNavigation from "../../common/sub-navigation";
import NewToolTip from "../../../../common/New-ToolTip";
import { getValidStep, updateStepsValidity } from "../../../text/data";

const Typical = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();

  const [step1Vals, setStep1Vals] = useState([]);
  const [step2Val, setStep2Val] = useState(null);
  const [step3Vals, setStep3Vals] = useState([]);
  const [step4Vals, setStep4Vals] = useState([]);

  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [typicalSSResult, setTypicalSSResult] = useState(false);
  const [allergenResult, setAllergenResult] = useState(false);
  const [showAllergen, setShowAllergen] = useState(false);

  const [completeTypicalAlert, setCompleteTypicalAlert] = useState(false);

  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);

  //Red Flag Alerts
  const [openAllergensAlert, setOpenAllergensAlert] = useState(false);
  const [openAllergens, setOpenAllergens] = useState(false);
  const [openMultiConditionDialog, setOpenMultiConditionDialog] =
    useState(false);

  //Red Flag Alert Input
  const [alertContent, setAlertContent] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [clickedEndService, setClickedEndService] = useState(false);
  const [alertConfirmation, setAlertConfirmation] = useState(0);
  const [alertExplanation, setAlertExplanation] = useState("");
  const [allergensAlertConfirmation, setAllergensAlertConfirmation] =
    useState(0);
  const [allergensAlertExplanation, setAllergensAlertExplanation] =
    useState("");
  const [clicked, setClicked] = useState(false);
  //state management
  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  useEffect(() => {
    if (completeSubStep) scrollX("typical", 550);
  }, [completeSubStep, allergenResult, typicalSSResult]);

  // Where values are saved
  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .signsAndSymptoms !== null &&
      (clinicalServiceObj.initialAssessment.initialAssessmentForm
        .signsAndSymptoms?.nasalSymptoms !== null ||
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .signsAndSymptoms?.visibleSymptoms !== null ||
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .signsAndSymptoms?.otherSymptoms !== null)
    ) {
      setClicked(false);
      setStep1Vals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms?.nasalSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.nasalSymptoms
          : ["none"]
      );
      setStep3Vals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms?.visibleSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.visibleSymptoms
          : ["none"]
      );
      setStep4Vals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms?.otherSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.otherSymptoms
          : ["none"]
      );
      setStep2Val(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms?.nasalCongestion
      );
      setCompleteTypicalAlert(true);
    }
  }, [clinicalServiceObj]);

  // When to show end card
  useEffect(() => {
    if (
      step1Vals.length > 0 &&
      step2Val != null &&
      step3Vals.length > 0 &&
      step4Vals.length > 0
    ) {
      setCompleteSubStep(true);
      handleAlerts(clicked);
    } else {
      setCompleteSubStep(false);
    }
  }, [step1Vals, step2Val, step3Vals, step4Vals]);

  const updateStepsVisibility = async (disable = false) => {
    const updatedServiceSteps = updateStepsValidity(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      disable
    );
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );

      const updatedSteps = {
        signsAndSymptoms: {
          nasalSymptoms: step1Vals == ["none"] ? [] : step1Vals,
          nasalCongestion: step2Val,
          visibleSymptoms: step3Vals == ["none"] ? [] : step3Vals,
          otherSymptoms: step4Vals == ["none"] ? [] : step4Vals,
          allergens: allergens,
          typicalConfirmationType: alertConfirmation,
          typicalExplanation: alertExplanation,
          allergensConfirmationType: allergensAlertConfirmation,
          allergensExplanation: allergensAlertExplanation,
        },
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        showReferral: showRef,
        serviceSteps: clinicalServiceSteps,
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  // Multi select answers
  const step1MultiSelection = (val) => {
    setClicked(true);
    if (val === "none") {
      !step1Vals.includes(val) ? setStep1Vals(["none"]) : setStep1Vals([]);
    } else {
      if (step1Vals.includes("none")) {
        setStep1Vals([val]);
      } else {
        step1Vals.includes(val)
          ? setStep1Vals(step1Vals.filter((item) => item !== val))
          : setStep1Vals([...step1Vals, val]);
      }
    }
  };

  const step3MultiSelection = (val) => {
    setClicked(true);
    if (val === "none") {
      !step3Vals.includes(val) ? setStep3Vals(["none"]) : setStep3Vals([]);
    } else {
      if (step3Vals.includes("none")) {
        setStep3Vals([val]);
      } else {
        step3Vals.includes(val)
          ? setStep3Vals(step3Vals.filter((item) => item !== val))
          : setStep3Vals([...step3Vals, val]);
      }
    }
    setTimeout(() => {
      scrollX("typical");
    }, 300);
  };

  const step4MultiSelection = (val) => {
    setClicked(true);
    if (val === "none") {
      !step4Vals.includes(val) ? setStep4Vals(["none"]) : setStep4Vals([]);
    } else {
      if (step4Vals.includes("none")) {
        setStep4Vals([val]);
      } else {
        step4Vals.includes(val)
          ? setStep4Vals(step4Vals.filter((item) => item !== val))
          : setStep4Vals([...step4Vals, val]);
      }
    }
  };

  // Handles "typical signs & symptoms alert"
  const handleAlerts = (clicked) => {
    if (
      step1Vals.includes("none") &&
      step2Val === "noCongestion" &&
      step3Vals.includes("none") &&
      step4Vals.includes("none")
    ) {
      setAlertContent(
        <>
          <p className="text-md-body font-semibold text-gray-600">
            No Typical Signs & Symptoms Identified.
          </p>
          <p className="text-sm-body text-gray-600">
            May consider differential diagnosis.
          </p>
        </>
      );
    } else if (
      step1Vals.includes("none") &&
      step2Val === "noCongestion" &&
      step3Vals.includes("none")
    ) {
      setAlertContent(
        <>
          <p className="text-body-md text-gray-600">
            No Nasal Symptoms & No Nasal Congestion Identified.
          </p>
        </>
      );
      if (step4Vals.includes("skinRash")) {
        setAlertContent(
          <>
            <p className="text-body-md text-gray-600">
              No Nasal Symptoms & No Nasal Congestion Identified.
            </p>
            <p className="text-body-md text-gray-600">
              <b>Skin Rashes or Hives</b> is not a common Allergic Rhinitis
              symptom.
            </p>
          </>
        );
      }
    } else if (
      step1Vals.includes("none") &&
      step2Val === "noCongestion" &&
      !step3Vals.includes("none")
    ) {
      setAlertContent(
        <>
          <p className="text-body-md text-gray-600">
            No Nasal Symptoms & No Nasal Congestion Identified.
          </p>
        </>
      );
      if (step4Vals.includes("skinRash")) {
        setAlertContent(
          <>
            <p className="text-body-md text-gray-600">
              No Nasal Symptoms & No Nasal Congestion Identified.
            </p>
            <p className="text-body-md text-gray-600">
              <b>Skin Rashes or Hives</b> is not a common Allergic Rhinitis
              symptom.
            </p>
          </>
        );
      }
    }
    // Show allergens popup thing
    if (!(step1Vals.includes("none") && step2Val === "noCongestion")) {
      setAllergenResult(clicked);
      setShowAllergen(true);
      setTypicalSSResult(clicked);
    } else {
      setAllergenResult(clicked);
      setShowAllergen(false);
      setTypicalSSResult(clicked);
    }
    // setTimeout(() => {
    //   scrollX("typical");
    // }, 300);
  };

  const handleContinue = (allergens) => {
    setAllergens(allergens);
    // If None is selected
    if (allergens.length === 0 || step4Vals.includes("skinRash")) {
      setAlertContent(
        <>
          <p className="text-sm text-gray-600">
            {step4Vals.includes("skinRash") && (
              <p className="text-body-md text-gray-600">
                <b>Skin Rashes or Hives</b> is not a common Allergic Rhinitis
                symptom
              </p>
            )}
            {allergens.length === 0 && (
              <>
                <b className="text-base">
                  Allergy symptoms did not start with exposure to allergens.
                </b>
                <br />
                <br />
                May consider alternative diagnosis.
                <br />
                Consider bacterial, fungal, or viral causes
              </>
            )}
          </p>
        </>
      );
      setOpenAllergensAlert(true);
    } else {
      setCompleteSubStep(true);
      // setAllergenResult(false);
      setCompleteTypicalAlert(true);
    }
  };

  useEffect(() => {
    if ((showAllergen && allergenResult) || (!showAllergen && typicalSSResult))
      setCompleteTypicalAlert(false);
  }, [showAllergen, allergenResult, typicalSSResult]);

  const scrollBack = () => {
    const element = document.getElementById("typical");
    element.scrollLeft -= 480;
  };
  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-2.5 mb-0.5">
                <h2 className="text-xs-display font-bold text-teal-300">
                  Typical Signs & Symptoms
                </h2>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="typical"
              >
                {/* step1 Nasal Symptoms*/}
                <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-green.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Nasal Symptoms
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg h-[51px] font-semibold text-gray-800">
                        {nasalSymptomsEnum.question}
                        <br />
                        <span className="font-normal">
                          {nasalSymptomsEnum.subquestion}{" "}
                        </span>
                      </p>
                      <div className="flex flex-col gap-2">
                        {nasalSymptomsEnum.options.map((option) => {
                          return (
                            <>
                              <button
                                onClick={() => {
                                  step1MultiSelection(option.value);
                                  setTimeout(() => {
                                    scrollX("typical", 900);
                                  }, 400);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                    ${
                                      step1Vals?.includes(option.value)
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}{" "}
                                  {option.value === "itchyNoseEyesThroat" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="nasalSymptoms"
                                      />
                                      <NewToolTip
                                        id={"nasalSymptoms"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 font-normal">
                                            Nasal itching is a key
                                            <br />
                                            sign of allergic rhinitis
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* step2 Nasal Congestion*/}
                <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-green.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Nasal Congestion
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg h-[51px] font-semibold text-gray-800">
                        {nasalCongestionEnum.question}
                        <br />
                        <span className="font-normal">
                          {nasalCongestionEnum.subquestion}{" "}
                        </span>
                      </p>
                      <div className="flex flex-col gap-2">
                        {nasalCongestionEnum.options.map((option) => {
                          return (
                            <>
                              <button
                                onClick={() => {
                                  setClicked(true);
                                  setStep2Val(option.value);
                                  setTimeout(() => {
                                    scrollX("typical", 900);
                                  }, 400);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                    ${
                                      step2Val === option.value
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}{" "}
                                  {option.value === "minorCongestion" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="minorCongestion"
                                      />
                                      <NewToolTip
                                        id={"minorCongestion"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            Early phase: nasal symptoms with
                                            <br />
                                            mild congestion
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                  {option.value === "moderateCongestion" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="majorCongestion"
                                      />
                                      <NewToolTip
                                        id={"majorCongestion"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            Late phase: nasal symptoms with
                                            <br />
                                            severe congestion
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* step3 Visible Symptoms */}
                <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-green.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Visible Symptoms
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg h-[51px] font-semibold text-gray-800">
                        {visibleSymptomsEnum.question}
                        <br />
                        <span className="font-normal">
                          {visibleSymptomsEnum.subquestion}{" "}
                        </span>
                      </p>
                      <div className="flex flex-col gap-2">
                        {visibleSymptomsEnum.options.map((option) => {
                          return (
                            <>
                              <button
                                onClick={() => {
                                  step3MultiSelection(option.value);
                                  setTimeout(() => {
                                    scrollX("typical", 900);
                                  }, 400);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                                  hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                    ${
                                      step3Vals?.includes(option.value)
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}{" "}
                                  {option.value === "allergicSalute" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="allergicSalute"
                                      />
                                      <NewToolTip
                                        id={"allergicSalute"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            This sign is more commonly seen in children;
                                            <br />
                                            hand frequently wipes the nose in
                                            an upward motion.
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                  {option.value === "allergicShiner" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="allergicShiner"
                                      />
                                      <NewToolTip
                                        id={"allergicShiner"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            Dark circles under the eyes
                                            <br />
                                            from nasal congestion
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* step4 Other Symptoms */}
                <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-green.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Other Symptoms
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg h-[51px] font-semibold text-gray-800">
                        {otherSymptomsEnum.question}
                        <br />
                        <span className="font-normal">
                          {otherSymptomsEnum.subquestion}{" "}
                        </span>
                      </p>
                      <div className="flex flex-col gap-2">
                        {otherSymptomsEnum.options.map((option) => {
                          return (
                            <>
                              <button
                                onClick={() => {
                                  step4MultiSelection(option.value);
                                  setTimeout(() => {
                                    scrollX("typical", 900);
                                  }, 400);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                                  hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                    ${
                                      step4Vals?.includes(option.value)
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* step5 Review Result */}
                {completeSubStep && (
                  <div>
                    <div className="h-[585px] pt-8 rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      {/* <div className="flex flex-row justify-start rounded-s">
                        <button
                          className="m-2 h-4 w-auto rounded-full bg-REELTeal border-REELTeal hover:bg-teal-300 text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-2 py-4"
                          onClick={() => {
                            scrollBack();
                            setTypicalSSResult(false);
                            setAllergenResult(false);
                            setCompleteSubStep(false);
                            setCompleteTypicalAlert(false);
                          }}
                        >
                          <ChevronLeftIcon className="w-4 h-4 text-white" />
                        </button>
                      </div> */}

                      {/* Typical Signs & Symptoms (1 & 2 === none) */}
                      {!showAllergen && (
                        <>
                          <div className="m-8 h-[420px] w-[420px] bg-blue-100 rounded-full flex items-center justify-center bg-opacity-40">
                            <ReactSVG
                              src={"/icons/analysis-pad.svg"}
                              className={``}
                            />
                          </div>

                          <div className="mx-7 my-5">
                            {typicalSSResult && (
                              <Button
                                className="w-full !px-2 !text-xl"
                                type="primary"
                                text="Typical Signs & Symptoms Results"
                                onClick={() => {
                                  setOpenMultiConditionDialog(true);
                                  setTypicalSSResult(false);
                                }}
                              />
                            )}
                            {!typicalSSResult && (
                              <h4 className="text-2xl text-REELTeal font-bold text-center justify-center flex">
                                Typical Signs & Symptoms Review Complete
                              </h4>
                            )}
                          </div>
                        </>
                      )}

                      {/* Review Symptoms & Allergens (1 & 2 != none) */}
                      {showAllergen && (
                        <>
                          <div className="ml-8 h-[420px] w-[420px] bg-green-50 rounded-full flex items-center justify-center bg-opacity-40">
                            <ReactSVG
                              src={"/icons/allergens.svg"}
                              className={``}
                            />
                          </div>
                          <div className="mx-7 my-5">
                            {allergenResult && (
                              <Button
                                className="w-full !h-[83px] !px-5 !text-xl"
                                type="primary"
                                text="Review Symptoms & Allergens"
                                onClick={() => {
                                  setOpenAllergens(true);
                                  setAllergenResult(false);
                                }}
                              />
                            )}
                            {!allergenResult && (
                              <>
                                <h4 className="text-2xl text-REELTeal font-bold text-center justify-center flex">
                                  Symptoms & Allergens
                                  <br />
                                  Review Complete
                                </h4>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Typical Signs & Symptoms Alerts */}
            <EndServiceModel
              open={openMultiConditionDialog}
              title="Typical Signs & Symptoms Alert"
              content={() => alertContent}
              handleClose={() => {
                setOpenMultiConditionDialog(false);
                setAlertConfirmation(0);
                setAlertExplanation("");
                setCompleteTypicalAlert(false);
                setTypicalSSResult(true);
              }}
              handleContinue={(ref, exp) => {
                setAlertConfirmation(
                  ref
                    ? AlertConfirmationTypes.ContinueWithRefer
                    : AlertConfirmationTypes.ContinueWithNoReferral
                );
                setAlertExplanation(exp);
                if (ref !== undefined) {
                  setShowRef(ref);
                }
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(false);
                if (openAllergensAlert) {
                  setTimeout(() => {
                    scrollX("typical");
                  }, 300);
                } else {
                  setCompleteSubStep(true);
                  setCompleteTypicalAlert(true);
                }
              }}
              handleEndService={() => {
                setShowRef(true);
                setAlertConfirmation(AlertConfirmationTypes.EndService);
                setAlertExplanation("");
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(true);
                setClickedEndService(true);
              }}
            />

            {/* Review Symptoms & Allergens Alert */}
            <AllergensAlert
              open={openAllergens}
              className="font-semibold text-gray-800"
              // Continue
              confirmAction={(allergens) => {
                setOpenAllergens(false);
                handleContinue(allergens);
              }}
              // X button, closes the modal
              onCross={() => {
                setOpenAllergens(false);
                setCompleteTypicalAlert(false);
                setAllergenResult(true);
              }}
              handleClose={() => {
                setOpenAllergens(false);
              }}
            />

            {/* If NONE selected on Review Symptoms & Allergens*/}
            <EndServiceModel
              open={openAllergensAlert}
              title="Symptoms & Allergens Alert"
              content={() => alertContent}
              handleClose={() => {
                setAllergensAlertConfirmation(0);
                setAllergensAlertExplanation("");
                setOpenAllergensAlert(false);
                setCompleteTypicalAlert(false);
                setAllergenResult(true);
              }}
              handleContinue={(ref, exp) => {
                setAllergensAlertConfirmation(
                  ref
                    ? AlertConfirmationTypes.ContinueWithRefer
                    : AlertConfirmationTypes.ContinueWithNoReferral
                );
                setAllergensAlertExplanation(exp);
                if (ref !== undefined) {
                  setShowRef(ref);
                }
                setOpenAllergensAlert(false);
                updateStepsVisibility(false);
                setCompleteSubStep(true);
                setCompleteTypicalAlert(true);
              }}
              handleEndService={() => {
                setShowRef(true);
                setAllergensAlertConfirmation(
                  AlertConfirmationTypes.EndService
                );
                setAllergensAlertExplanation("");
                setOpenAllergensAlert(false);
                updateStepsVisibility(true);
                setClickedEndService(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] 
              ${!completeTypicalAlert && "opacity-40 pointer-events-none"}
            `}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Typical;
